/**
 * MypageReviewContainer
 * --
 */
import React from 'react';
import MypageReviewPresenter from './MypageReviewPresenter';
import { useUserDataAction, useUserDataStack, useUserStack } from 'store';
import { useGetParcelServices } from 'hooks/ParcelServiceHooks';
import { useDeleteReview } from 'hooks/ReviewHooks';
import { encrypt } from 'utils/Crypto';
import MessageAlert from 'utils';
import { useGetRentals } from 'hooks/RentalHooks';

const TAP = [
  {
    label: '리뷰작성',
    value: 'BEFORE',
  },
  {
    label: '나의리뷰',
    value: 'AFTER',
  },
];

const MypageReviewContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, screenSize } = props;

  /* ===== Store ===== */
  const userInfo = useUserStack();

  const { reviews } = useUserDataStack();
  const userDataAction = useUserDataAction();

  /* ===== STATE ===== */
  const [selectedTab, setSelectedTab] = React.useState('BEFORE');
  const [isLoading, setIsLoading] = React.useState(false);

  /* ===== Mutate ===== */
  const { deleteReview } = useDeleteReview();

  /* ===== Data ===== */
  // 배송/정기배송 목록
  const { data: parcelList } = useGetParcelServices(
    {
      user_id: userInfo.user_id,
      join_tables: '["refund"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      enabled: !!userInfo,
      initialData: [],
    },
  );
  // 렌탈 목록
  const { data: rentalList } = useGetRentals(
    {
      user_id: userInfo.user_id,
      join_tables: '["refund"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      enabled: !!userInfo,
      initialData: [],
    },
  );

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 리뷰 생성/수정/삭제
   * --
   * @param {string} t
   * @param {object} item
   */
  const handleSubmit = async (t = 'update', item) => {
    const submitType = t.toLocaleUpperCase();

    const confirm = (msg) => window.confirm(msg);

    try {
      const productType = item?.parcel_service_id ? 'parcel_service' : 'rental';

      switch (submitType) {
        case 'CREATE': {
          const productId = item?.product?.product_id ?? item?.product_id;

          if (!productId) {
            MessageAlert.warning(
              '상품 정보를 확인할 수 없어 리뷰를 작성할 수 없습니다.',
            );
            return;
          }

          navigate(
            `/mypage/reviewform?orderNum=${encodeURIComponent(
              encrypt(item?.[`${productType}_id`]),
            )}&product=${encodeURIComponent(
              encrypt(productId),
            )}&type=${productType}`,
          );
          return;
        }
        case 'UPDATE':
          if (!confirm('리뷰를 수정하시겠습니까?')) return;
          navigate(
            `/mypage/reviewform?orderNum=${encodeURIComponent(
              encrypt(item?.[`${productType}_id`]),
            )}&product=${encodeURIComponent(
              encrypt(item?.product_id),
            )}&type=${productType}&review=${encodeURIComponent(
              encrypt(item?.review_id),
            )}`,
          );
          return;
        case 'DELETE':
          if (
            !confirm(
              '리뷰 삭제 시 적립 혜택이 회수됩니다.\n리뷰를 정말 삭제하시겠습니까?',
            )
          )
            return;
          setIsLoading(true);
          const res = await deleteReview(item?.review_id);
          if (res.status !== 200) {
            MessageAlert.error('리뷰를 삭제하는데 실패하였습니다.');
            return;
          }
          userDataAction.removeData('reviews', 'review_id', item);
          MessageAlert.success('리뷰가 삭제되었습니다.');
          return;
        default:
          return;
      }
    } catch (err) {
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 리뷰 목록 정제
   * --
   */
  const ReviewList = React.useMemo(() => {
    if (!parcelList.length && !rentalList.length)
      return {
        BEFORE: [],
        AFTER: [],
      };

    // NOTE: 리뷰 작성 가능한 목록 추출
    const newParcelList = parcelList?.filter((order) => {
      const { type, regular_payment_info, delivery_status } = order;

      if (type === 'SUBSCRIPTION') {
        const isExist = regular_payment_info.some(
          (regular) => regular?.delivery_status === 'DELIVERED',
        );

        return isExist;
      } else {
        const isConfirmed = delivery_status === 'DELIVERED';

        return isConfirmed;
      }
    });

    // NOTE: 리뷰 작성 가능한 렌탈 목록 추출
    const newRentalList = rentalList?.filter(
      (rental) => rental?.delivery_status === 'DELIVERED',
    );

    const concatList = newParcelList.concat(newRentalList);

    const newOrderList = concatList.map((order) => {
      const key = order.parcel_service_id ? 'parcel_service_id' : 'rental_id';

      return {
        ...order,
        review: reviews.find((review) => review[key] === order[key]),
      };
    });

    const newReviewList = newOrderList.reduce(
      (acc, cur) => {
        const { review } = cur;

        if (review) {
          acc['AFTER'].push(cur);
        } else {
          acc['BEFORE'].push(cur);
        }

        return acc;
      },
      {
        BEFORE: [],
        AFTER: [],
      },
    );

    newReviewList['BEFORE'].sort((a, b) => b.created_at - a.created_at);
    newReviewList['AFTER'].sort(
      (a, b) => b.review.created_at - a.review.created_at,
    );

    return newReviewList;
  }, [parcelList, reviews, rentalList]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageReviewPresenter
        user={userInfo}
        order={ReviewList}
        tapItem={TAP}
        navigate={navigate}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        screenSize={screenSize}
        onSubmit={handleSubmit}
        isLoading={isLoading}
      />
    </>
  );
};

export default MypageReviewContainer;
